import totalScans from '@root/assets/svg/k8ScanHistory/totalScans.svg';
import scanErrors from '@root/assets/svg/k8ScanHistory/scanErrors.svg';
import lastScan from '@root/assets/svg/k8ScanHistory/lastScan.svg';
import virusDbFleet from '@root/assets/svg/k8ScanHistory/virusDbFleet.svg';
import ContentBox from '@root/components/v2/content/ContentBox';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import React, { useMemo } from 'react';
import { TopRowSkeleton } from '../../../skeleton';
import '../style.scss';

const TONE_COLOR = {
  success: '#2f9e44',
  warning: '#dd6b20',
  error: '#e53e3e',
};

const getFleetStatus = fleet => {
  const { total_engines = 0, engines_current = 0, engines_stale = 0, engines_unreachable = 0 } = fleet || {};
  if (engines_unreachable > 0) return { label: 'unreachable', tone: 'error' };
  if (engines_stale > 0) return { label: 'stale', tone: 'warning' };
  if (total_engines > 0 && engines_current === total_engines) return { label: 'current', tone: 'success' };
  return { label: 'partial', tone: 'warning' };
};

const ScanHistoryStats = ({ data, isLoading }) => {
  const overviewData = useMemo(() => {
    if (isLoading || !data) return [];
    const items = [
      { title: 'Total Scans', count: data.totalScans ?? 0, image: totalScans, infoText: 'All Time Scans' },
      { title: 'Scan Errors', count: data.scanErrors ?? 0, image: scanErrors, infoText: 'Failed Workloads' },
      { title: 'Last Scan', count: data.lastScan ?? '-', image: lastScan, infoText: 'Most Recent Run' },
    ];

    const fleet = data.virus_db_fleet;
    if (fleet?.available) {
      const status = getFleetStatus(fleet);
      items.push({
        title: 'Virus DB Fleet',
        image: virusDbFleet,
        isFleet: true,
        badge: fleet.badge?.replace('/', ' / ') ?? `${fleet.engines_current ?? 0} / ${fleet.total_engines ?? 0}`,
        statusLabel: status.label,
        statusColor: TONE_COLOR[status.tone],
        signature: fleet.latest_db_version,
        updatedAgo: fleet.updated_ago,
        infoText: `ClamAV ${fleet.engine_version ?? ''}${fleet.status ? ` · ${fleet.status}` : ''}`.trim(),
      });
    }

    return items;
  }, [data, isLoading]);
  return (
    <ContentBox
      title="Scan History Overview"
      containerClassName={'border-box'}
      paddingMode={'content'}
      style={{ marginBottom: '15px' }}
      titleContainerStyle={{
        borderRadius: '5px 5px 0 0',
        height: '40px',
      }}
      titleSize="medium">
      <div className="KubernetesIdentity__Container">
        {isLoading || !overviewData.length ? (
          <TopRowSkeleton count={4} />
        ) : (
          <div className="KubernetesIdentity__Wrapper">
            {overviewData.map((item, idx) => (
              <Tooltip key={item.title} title={item.infoText}>
                <div
                  className="KubernetesIdentity__Box"
                  style={{
                    borderRight: idx === overviewData?.length - 1 ? 'none' : '1px solid var(--k8s-identity-border)',
                  }}>
                  <div className="KubernetesIdentity__BoxInner">
                    {item.isFleet ? (
                      <div className="KuberenetesIdentity__Text" style={{ gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span>
                            <img height={'32px'} width={'32px'} src={item.image} alt={item.title} />
                          </span>
                          <p className="m-0 KuberenetesText">
                            {item.title} · sig {item.signature} · updated {item.updatedAgo}
                          </p>
                        </div>
                        <p className="m-0 KuberenetesCount">
                          {item.badge} {item.statusLabel}
                        </p>
                      </div>
                    ) : (
                      <div className="KuberenetesIdentity__Text" style={{ gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span>
                            <img height={'32px'} width={'32px'} src={item.image} alt={item.title} />
                          </span>
                          <p className="m-0 KuberenetesText">{item.title}</p>
                        </div>
                        <p className="m-0 KuberenetesCount">{item.count}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    </ContentBox>
  );
};

export default React.memo(ScanHistoryStats);
